<template>
    <section
        :id="slice.primary.anchorName || undefined"
        class="slice slice-content-grid slice-content-grid--side"
        aria-label="content-grid"
        data-testid="slice-content-grid-side"
    >
        <div :class="['slice-content', 'slice-content-grid--side__content', {'slice-content-grid--side__content--reverse': slice.primary.reverse}]">
            <div class="slice-content-grid--side__content-left">
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.title)"
                    :field="slice.primary.title"
                    class="mui-title"
                    data-testid="slice-content-grid-title"
                />
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.description)"
                    :field="slice.primary.description"
                    class="slice-description"
                    data-testid="slice-content-grid-description"
                />
            </div>
            <div class="slice-content-grid--side__content-right" data-testid="slice-content-grid-side-items">
                <div
                    v-for="(item, idx) in slice.items"
                    :key="idx"
                    :class="['slice-content-grid--side__item', {'slice-content-grid--side__item--has-link': isFilled.link(item.link)}]"
                    :data-testid="`slice-content-grid-side-item--${idx}`"
                >
                    <Component
                        :is="isFilled.link(item.link) ? NuxtLink : 'div'"
                        v-slot="{href, navigate} = {}"
                        :to="isFilled.link(item.link) && item.link.url ? $withLocalePrefix(item.link.url) : undefined"
                        external
                        custom
                    >
                        <div
                            class="slice-content-grid--side__item-content"
                            :data-testid="`slice-content-grid-side-item-content--${idx}`"
                            @click="isFilled.link(item.link) ? navigate() : undefined"
                        >
                            <div v-if="isFilled.image(item.image)">
                                <PrismicImage :field="item.image" :alt="item.image.alt" :data-testid="`slice-content-grid-side-item-image--${idx}`" />
                            </div>
                            <div class="slice-content-grid--side__item-content-right">
                                <a v-if="isFilled.link(item.link)" :href="href || undefined">
                                    <h4
                                        v-if="isFilled.keyText(item.title)"
                                        class="mui-title"
                                        :data-testid="`slice-content-grid-side-item-title--${idx}`"
                                    >
                                        {{ item.title }}
                                    </h4>
                                </a>
                                <h4 v-else class="mui-title" :data-testid="`slice-content-grid-side-item-title--${idx}`">
                                    {{ item.title }}
                                </h4>
                                <p v-if="isFilled.keyText(item.text)" :data-testid="`slice-content-grid-side-item-text--${idx}`">{{ item.text }}</p>
                                <span
                                    v-if="isFilled.link(item.link) && item.link.text"
                                    :data-testid="`slice-content-grid-side-item-link-label--${idx}`"
                                    class="mui-c-link"
                                >
                                    {{ item.link.text }}
                                </span>
                            </div>
                        </div>
                    </Component>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';

    import type {ContentGridSlice} from '@prismicio-types';
    import {NuxtLink} from '#components';

    type ContentGridSideInZone = Extract<ContentGridSlice, {variation: 'contentGridSide'}>;
    defineProps(getSliceComponentProps<ContentGridSideInZone>());
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-content-grid--side {
        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-8);

            @include mq.screen_md(min) {
                flex-direction: row;
                align-items: center;
                gap: var(--joy-core-spacing-10);

                &--reverse {
                    flex-direction: row-reverse;
                }
            }
        }

        .slice-content {
            text-align: left;
        }

        &__content-left {
            flex: 0 34%;
        }

        &__content-right {
            display: grid;
            padding: var(--joy-core-spacing-2) 0;
            grid-template-rows: repeat(2, auto);
            grid-auto-flow: column;
            grid-auto-columns: minmax(310px, calc(100% - 120px));
            gap: var(--joy-core-spacing-4);
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }

            @include mq.screen_sm(min) {
                flex: 0;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                grid-template-rows: repeat(2, auto);
                grid-auto-flow: row;
                grid-auto-columns: unset;
                width: 100%;
            }

            @include mq.screen_md(min) {
                flex: auto;
            }

            @include mq.screen_lg(min) {
                flex: 1;
                gap: var(--joy-core-spacing-4);
            }
        }

        &__item {
            display: flex;
            flex-direction: column;
            min-width: 0;
            border-radius: var(--joy-core-radius-4);
            padding: var(--joy-core-spacing-6);
            border: 1px solid var(--joy-color-neutral-30);
            background-color: var(--joy-color-background-default);
            transition: box-shadow var(--joy-transition-duration-default) var(--joy-transition-timing-function);

            &:hover {
                box-shadow: var(--joy-core-elevation-1);
            }

            a {
                text-decoration: none;
                color: inherit;
            }

            &--has-link {
                cursor: pointer;
            }

            &-content {
                display: flex;
                align-items: flex-start;
                gap: var(--joy-core-spacing-5);
                flex: 1;
                min-height: 0;

                &-right {
                    display: flex;
                    flex-direction: column;
                    gap: var(--joy-core-spacing-2);
                    height: 100%;

                    .mui-c-link {
                        margin-top: var(--joy-core-spacing-3);
                    }

                    p {
                        @include text.getFontBySize(primary-300);

                        letter-spacing: 0.2px;
                        color: var(--joy-color-neutral-40);
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;

                        @include mq.screen_sm(min) {
                            display: block;
                        }
                    }
                }

                img {
                    border-radius: var(--joy-core-radius-4);
                    width: 73px;
                    height: 73px;
                    object-fit: cover;
                }
            }
        }
    }
</style>
